import { inject, Injectable } from '@angular/core';
import { EduContentTocService, WordFormInterface, WordInterface } from '@campus/dal';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { ReadSpeakerService } from '../readspeaker';
import { ReadspeakerConfig } from '../readspeaker/readspeaker.interface';

export interface ReadSpeakerState {
  enabled: boolean;
  config?: ReadspeakerConfig;
}

export interface ImageDictionarySource {
  tocId$: Observable<number | null>;
  readSpeakerEnabled$: Observable<boolean>;
  readSpeakerConfigId$: Observable<number | null>;
  searchTerm$?: Observable<string>;
}

export interface ImageDictionaryWord extends WordInterface {
  /** Stable DOM id for the word's card (the ReadSpeaker read target). */
  cardId: string;
}

export interface ImageDictionaryViewModel {
  words$: Observable<ImageDictionaryWord[]>;
  filteredWords$: Observable<ImageDictionaryWord[]>;
  readSpeaker$: Observable<ReadSpeakerState>;
}

/**
 * Shared logic for the image dictionary ("Beeldwoordenboek"): fetching the words
 * for a TOC, resolving the ReadSpeaker config, filtering and reading words aloud.
 *
 * Used by both the image-dict slideout and the methods image-dictionary tab so the
 * behaviour stays identical between the two presentations.
 */
@Injectable({ providedIn: 'root' })
export class ImageDictionaryService {
  private eduContentTocService = inject(EduContentTocService);
  private readSpeakerService = inject(ReadSpeakerService);

  /** Fetch the (sorted) image-dictionary words for a TOC. */
  public getWordsForToc$(eduContentTOCId: number): Observable<WordInterface[]> {
    return this.eduContentTocService.getWordsForToc(eduContentTOCId);
  }

  /**
   * Build the derived image-dictionary streams from a caller's source streams.
   *
   * Pure: each call returns an independent stream graph, so the singleton holds no
   * per-consumer state. ReadSpeaker config is resolved lazily — only when enabled.
   */
  public createViewModel(source: ImageDictionarySource): ImageDictionaryViewModel {
    const words$ = source.tocId$.pipe(
      filter((tocId): tocId is number => tocId != null),
      switchMap((tocId) => this.getWordsForToc$(tocId)),
      map((words) => words.map((word) => ({ ...word, cardId: this.getCardId(word.id!) }))),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const searchTerm$ = source.searchTerm$ ?? of('');

    const filteredWords$ = combineLatest([words$, searchTerm$]).pipe(
      map(([words, searchTerm]) => this.filterWords(words, searchTerm))
    );

    const readSpeaker$ = combineLatest([source.readSpeakerEnabled$, source.readSpeakerConfigId$]).pipe(
      switchMap(
        ([enabled, configId]): Observable<ReadSpeakerState> =>
          enabled
            ? this.getReadSpeakerConfig$(configId ?? undefined).pipe(map((config) => ({ enabled, config })))
            : of<ReadSpeakerState>({ enabled: false })
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return { words$, filteredWords$, readSpeaker$ };
  }

  /** Resolve the ReadSpeaker config for a book's configured ReadSpeaker id. */
  private getReadSpeakerConfig$(readSpeakerConfigId?: number): Observable<{ lang: string; voice: string }> {
    return this.readSpeakerService.getReadspeakerConfig(readSpeakerConfigId);
  }

  /** Filter words by their label or any of their word-forms. */
  public filterWords<T extends WordInterface>(words: T[], searchTerm: string): T[] {
    if (!searchTerm) return words ?? [];

    const lowerSearchTerm = searchTerm.toLowerCase();
    return (words ?? []).filter(
      (word) =>
        word.label?.toLowerCase().includes(lowerSearchTerm) ||
        (word.forms &&
          JSON.parse(word.forms)?.some((form: WordFormInterface) =>
            form.label?.toLowerCase().includes(lowerSearchTerm)
          ))
    );
  }

  /** Stable DOM id for a word card, used as the ReadSpeaker read target. */
  public getCardId(wordId: number): string {
    return `word-card-${wordId}`;
  }

  /** Read a word card aloud via ReadSpeaker — no-op when ReadSpeaker is disabled or unconfigured. */
  public readText(wordId: number, readSpeaker: ReadSpeakerState): void {
    if (!readSpeaker?.enabled || !readSpeaker.config) {
      return;
    }

    this.readSpeakerService.readTextById(this.getCardId(wordId), readSpeaker.config);
  }
}
